import React, { useEffect, useState } from 'react';
import { useAppState } from '../state/AppState';
import { fetchProviderImage } from '../services/providerImageService';
import { getProviderNameFromAvailability } from '../services/availabilityDataService';

interface ProviderImageProps {
  maxWidth?: number;
  maxHeight?: number;
  borderRadius?: number;
  className?: string;
  style?: React.CSSProperties;
  alt?: string;
}

const ProviderImage: React.FC<ProviderImageProps> = ({
  maxWidth = 200,
  maxHeight = 200,
  borderRadius = 32,
  className,
  style,
  alt
}) => {
  const { state } = useAppState();
  const [providerImageUrl, setProviderImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  const providerName = getProviderNameFromAvailability(state.availabilities, null);
  
  // Get provider code from availability data (prioritize availability data over URL params)
  const getProviderCode = () => {
    // First priority: provider code from availability data
    if (state.availabilities && state.availabilities.length > 0) {
      return state.availabilities[0].providerCode;
    }
    
    
    return null;
  };

  useEffect(() => {
    const loadProviderImage = async () => {
      setLoading(true);
      
      
      // Otherwise, fetch from API using provider code
      const providerCode = getProviderCode();
      if (providerCode) {
        try {
          const imageUrl = await fetchProviderImage(providerCode);
          setProviderImageUrl(imageUrl);
        } catch (error) {

        }
      }
      
      setLoading(false);
    };

    loadProviderImage();
  }, [state.availabilities]);

  // Create placeholder image when no provider image is available
  const createPlaceholderImage = (): string => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = maxWidth;
      canvas.height = maxHeight;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        // Fallback to a simple data URL with initials
        return `data:image/svg+xml;base64,${btoa(`
          <svg width="${maxWidth}" height="${maxHeight}" xmlns="http://www.w3.org/2000/svg">
            <rect width="100%" height="100%" fill="#e3f2fd"/>
            <text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" 
                  font-family="Arial, sans-serif" font-size="${Math.min(maxWidth, maxHeight) * 0.3}" 
                  font-weight="bold" fill="#1976d2">
              ${providerName.split(' ').map(name => name.charAt(0)).join('').toUpperCase().slice(0, 2) || 'MD'}
            </text>
          </svg>
        `)}`;
      }
      
      // Create gradient background
      const gradient = ctx.createLinearGradient(0, 0, maxWidth, maxHeight);
      gradient.addColorStop(0, '#e3f2fd');
      gradient.addColorStop(1, '#bbdefb');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, maxWidth, maxHeight);
      
      // Add provider initials or generic icon
      ctx.fillStyle = '#1976d2';
      ctx.font = `bold ${Math.min(maxWidth, maxHeight) * 0.3}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Get initials from provider name
      const initials = providerName
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
      
      ctx.fillText(initials || 'MD', maxWidth / 2, maxHeight / 2);
      
      return canvas.toDataURL();
    } catch (error) {
      // Ultimate fallback - return a simple colored square
      return `data:image/svg+xml;base64,${btoa(`
        <svg width="${maxWidth}" height="${maxHeight}" xmlns="http://www.w3.org/2000/svg">
          <rect width="100%" height="100%" fill="#e3f2fd"/>
          <text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" 
                font-family="Arial, sans-serif" font-size="${Math.min(maxWidth, maxHeight) * 0.3}" 
                font-weight="bold" fill="#1976d2">MD</text>
        </svg>
      `)}`;
    }
  };

  // Use provider image if available, otherwise use placeholder
  const providerPhoto = providerImageUrl || createPlaceholderImage();
  
  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    maxWidth: `${maxWidth}px`,
    maxHeight: `${maxHeight}px`,
    borderRadius: `${borderRadius}px`,
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
    ...style
  };

  if (loading) {
    return (
      <div
        className={className}
        style={{
          ...imageStyle,
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666'
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <img
      src={providerPhoto}
      alt={alt || providerName}
      className={className}
      style={imageStyle}
    />
  );
};

export default ProviderImage;
